import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { sendSidebarContact, emailMessages } from '../services/emailService';

const CenterModal = ({ isOpen, onClose }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: 'Allgemeine Anfrage',
        message: '',
        propertyType: '',
        budget: '',
        priority: 'normal',
        datenschutzAkzeptiert: false
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [errors, setErrors] = useState({});

    // Handle escape key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({});

        // Validierung: Datenschutz muss akzeptiert sein
        if (!formData.datenschutzAkzeptiert) {
            setErrors({ datenschutz: 'Bitte akzeptieren Sie die Datenschutzerklärung' });
            return;
        }

        setIsSubmitting(true);

        try {
            await sendSidebarContact(formData);
            setSubmitted(true);

            // Reset form after success
            setTimeout(() => {
                setSubmitted(false);
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    subject: 'Allgemeine Anfrage',
                    message: '',
                    propertyType: '',
                    budget: '',
                    priority: 'normal',
                    datenschutzAkzeptiert: false
                });
                setErrors({});
                onClose();
            }, 2000);
        } catch (error) {
            console.error('Fehler beim Senden des Kontaktformulars:', error);
            alert(emailMessages.error.general);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (typeof document === 'undefined') return null;

    return createPortal(
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-md z-[99998]"
                        onClick={onClose}
                        style={{ zIndex: 99998 }}
                    />

                    {/* Modal Container */}
                    <div
                        className="fixed inset-0 z-[99999] flex items-center justify-center p-4 sm:p-6 overflow-y-auto pointer-events-none"
                        style={{ zIndex: 99999 }}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            transition={{ duration: 0.3, ease: "easeOut" }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl my-auto relative pointer-events-auto overflow-hidden flex flex-col md:flex-row"
                            style={{ zIndex: 99999 }}
                        >
                            {/* Left Side: Contact Info & Branding (Desktop) / Top (Mobile) */}
                            <div className="w-full md:w-2/5 bg-gray-50 p-8 flex flex-col justify-between relative overflow-hidden">
                                {/* Decorative Circle */}
                                <div className="absolute top-0 right-0 -mr-20 -mt-20 w-64 h-64 bg-green-100 rounded-full opacity-50 blur-3xl"></div>
                                <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-64 h-64 bg-green-100 rounded-full opacity-50 blur-3xl"></div>

                                <div className="relative z-10">
                                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Kontaktieren Sie uns</h2>
                                    <p className="text-gray-600 mb-8">
                                        Haben Sie Fragen oder wünschen Sie ein Angebot? Wir sind für Sie da.
                                    </p>

                                    <div className="space-y-6">
                                        <a href="tel:+4987114241333" className="flex items-start space-x-4 group">
                                            <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center text-green-600 group-hover:text-green-700 group-hover:scale-110 transition-all">
                                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                                            </div>
                                            <div>
                                                <p className="font-semibold text-gray-900">Rufen Sie uns an</p>
                                                <p className="text-gray-600">+49 871 14241333</p>
                                            </div>
                                        </a>

                                        <a href="mailto:info@mgs-services.de" className="flex items-start space-x-4 group">
                                            <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center text-green-600 group-hover:text-green-700 group-hover:scale-110 transition-all">
                                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                                            </div>
                                            <div>
                                                <p className="font-semibold text-gray-900">Schreiben Sie uns</p>
                                                <p className="text-gray-600">info@mgs-services.de</p>
                                            </div>
                                        </a>
                                    </div>
                                </div>

                                <div className="relative z-10 mt-8 md:mt-0">
                                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                                        <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                                        <span>Antwort innerhalb von 24 Stunden</span>
                                    </div>
                                </div>
                            </div>

                            {/* Right Side: Form */}
                            <div className="w-full md:w-3/5 p-8 relative">
                                <button
                                    onClick={onClose}
                                    className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>

                                <form onSubmit={handleSubmit} className="space-y-5 mt-2">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                required
                                                className="w-full px-4 py-3 rounded-xl bg-gray-50 border-transparent focus:bg-white focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all outline-none"
                                                placeholder="Ihr Name"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">E-Mail</label>
                                            <input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                required
                                                className="w-full px-4 py-3 rounded-xl bg-gray-50 border-transparent focus:bg-white focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all outline-none"
                                                placeholder="ihre@email.de"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Betreff</label>
                                        <select
                                            name="subject"
                                            value={formData.subject}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-3 rounded-xl bg-gray-50 border-transparent focus:bg-white focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all outline-none appearance-none"
                                        >
                                            <option value="Allgemeine Anfrage">Allgemeine Anfrage</option>
                                            <option value="Gebäudereinigung">Gebäudereinigung</option>
                                            <option value="Büroreinigung">Büroreinigung</option>
                                            <option value="Treppenhausreinigung">Treppenhausreinigung</option>
                                            <option value="Fensterreinigung">Fensterreinigung</option>
                                            <option value="Fliesenlegen">Fliesenlegen</option>
                                            <option value="Angebot anfordern">Angebot anfordern</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Nachricht</label>
                                        <textarea
                                            name="message"
                                            value={formData.message}
                                            onChange={handleInputChange}
                                            required
                                            rows={4}
                                            className="w-full px-4 py-3 rounded-xl bg-gray-50 border-transparent focus:bg-white focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all outline-none resize-none"
                                            placeholder="Wie können wir Ihnen helfen?"
                                        />
                                    </div>

                                    <div className="flex items-start">
                                        <input
                                            type="checkbox"
                                            id="datenschutz"
                                            name="datenschutzAkzeptiert"
                                            checked={formData.datenschutzAkzeptiert}
                                            onChange={handleInputChange}
                                            className="mt-1 w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                        />
                                        <label htmlFor="datenschutz" className="ml-2 text-sm text-gray-500">
                                            Ich stimme der <a href="/datenschutz" className="text-green-600 hover:underline">Datenschutzerklärung</a> zu.
                                        </label>
                                    </div>
                                    {errors.datenschutz && <p className="text-red-500 text-xs">{errors.datenschutz}</p>}

                                    <button
                                        type="submit"
                                        disabled={isSubmitting || submitted}
                                        className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3.5 rounded-xl transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-green-500/30 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
                                    >
                                        {isSubmitting ? (
                                            <span className="flex items-center">
                                                <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 818-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                                Senden...
                                            </span>
                                        ) : submitted ? (
                                            <span className="flex items-center">
                                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                                Gesendet!
                                            </span>
                                        ) : (
                                            "Nachricht senden"
                                        )}
                                    </button>
                                </form>
                            </div>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>,
        document.body
    );
};

export default CenterModal;
